
import { Type, type Static } from '@sinclair/typebox';
import * as TypeBox from '@sinclair/typebox';
import { type FastifySchema } from 'fastify';


/**
 * Create a transform schema for a date string that is converted to a Date object.
 */
const DateTransform = (options?: TypeBox.StringOptions) =>
  Type.Transform(Type.String(options))
    .Decode((value) => new Date(value)) // decode: number to Date
    .Encode((value) => value.toISOString());


export interface RouteConfig {
  method: string;
  path: string;
  config: {
    schema: {
      params?: FastifySchema;
      querystring?: FastifySchema;
      body?: FastifySchema;
      response?: {
        [statusCode: number]: FastifySchema;
      };
      headers?: FastifySchema;
    };

    // OpenGov claims required for the route
    ogClaimsRequired?: string[];
  };
}

export const JSONAPIErrorSchema = Type.Object({
    errors: Type.Array(
      Type.Object({
        id: Type.String({ format: "uuid" }),
        status: Type.String(),
        title: Type.String(),
        detail: Type.String(),
      })
    ),
  });

  export type JSONAPIError = Static<typeof JSONAPIErrorSchema>;

export const APIError = Type.Object({
    error: Type.String(),
    message: Type.Optional(Type.String()),
    source: Type.Optional(Type.Object({
      pointer: Type.String()
    })),
    code: Type.Optional(Type.String())
  });

  export type APIError = Static<typeof APIError>;

export const GetAppsParamsSchema = Type.Object({});

export const GetAppsQuerySchema = Type.Object({
'filter[ownerUserId]': Type.Optional(Type.String()),
'filter[name]': Type.Optional(Type.String()),
'filter[entityUuid]': Type.Optional(Type.String()),
'page[number]': Type.Optional(Type.Number()),
'page[size]': Type.Optional(Type.Number())
});

export const GetAppsResponseSchema = Type.Object({
data: Type.Array(
Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('apps')
]),
attributes: Type.Object({
name: Type.String(),
appUserId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
serviceAccountUuid: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
entityId: Type.Number(),
entityUuid: Type.String(),
entityName: Type.String(),
clientId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
clientSecret: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
signingSecret: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
url: Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
]),
installUrl: Type.Optional(Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
])),
ownerUserId: Type.Union([
Type.String(),
Type.Null()
]),
description: Type.Union([
Type.String(),
Type.Null()
]),
deleted_at: Type.Optional(Type.Union([
DateTransform({ format: 'date-time' }),
Type.Null()
])),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: DateTransform({ format: 'date-time' }),
logoUrl: Type.Optional(Type.String({ format: 'uri' })),
logoFileId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
]))
})
})
) /* array of items */,
links: Type.Object({
self: Type.Optional(Type.String({ format: 'uri-reference' })),
first: Type.Optional(Type.String({ format: 'uri-reference' })),
prev: Type.Optional(Type.String({ format: 'uri-reference' })),
next: Type.Optional(Type.String({ format: 'uri-reference' })),
last: Type.Optional(Type.String({ format: 'uri-reference' }))
}),
meta: Type.Object({
page: Type.Number(),
size: Type.Number(),
totalPages: Type.Number(),
totalRecords: Type.Number()
})
});

export type GetAppsParams = Static<typeof GetAppsParamsSchema>;
export type GetAppsQuery = Static<typeof GetAppsQuerySchema>;
export type GetAppsResponse = Static<typeof GetAppsResponseSchema>;

export const getApps = {
  method: 'GET',
  path: '/apps',
  config: {
  schema: {
  params: GetAppsParamsSchema,
  querystring: GetAppsQuerySchema,
  response: {
  200: Type.Object({
data: Type.Array(
Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('apps')
]),
attributes: Type.Object({
name: Type.String(),
appUserId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
serviceAccountUuid: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
entityId: Type.Number(),
entityUuid: Type.String(),
entityName: Type.String(),
clientId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
clientSecret: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
signingSecret: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
url: Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
]),
installUrl: Type.Optional(Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
])),
ownerUserId: Type.Union([
Type.String(),
Type.Null()
]),
description: Type.Union([
Type.String(),
Type.Null()
]),
deleted_at: Type.Optional(Type.Union([
DateTransform({ format: 'date-time' }),
Type.Null()
])),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: DateTransform({ format: 'date-time' }),
logoUrl: Type.Optional(Type.String({ format: 'uri' })),
logoFileId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
]))
})
})
) /* array of items */,
links: Type.Object({
self: Type.Optional(Type.String({ format: 'uri-reference' })),
first: Type.Optional(Type.String({ format: 'uri-reference' })),
prev: Type.Optional(Type.String({ format: 'uri-reference' })),
next: Type.Optional(Type.String({ format: 'uri-reference' })),
last: Type.Optional(Type.String({ format: 'uri-reference' }))
}),
meta: Type.Object({
page: Type.Number(),
size: Type.Number(),
totalPages: Type.Number(),
totalRecords: Type.Number()
})
}),
  400: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  401: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  403: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  406: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  500: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
},
  headers: Type.Object({}),
},
}
} as const satisfies RouteConfig;

export const CreateAppParamsSchema = Type.Object({});

export const CreateAppBodySchema = Type.Object({
data: Type.Object({
type: Type.Union([
Type.Literal('apps')
]),
attributes: Type.Object({
name: Type.String(),
url: Type.Optional(Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
])),
description: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
entityId: Type.Number(),
entityUuid: Type.String()
})
})
});

export const CreateAppResponseSchema = Type.Object({
data: Type.Optional(Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('apps')
]),
attributes: Type.Object({
name: Type.String(),
appUserId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
serviceAccountUuid: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
entityId: Type.Number(),
entityUuid: Type.String(),
entityName: Type.String(),
clientId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
clientSecret: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
signingSecret: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
url: Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
]),
installUrl: Type.Optional(Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
])),
ownerUserId: Type.Union([
Type.String(),
Type.Null()
]),
description: Type.Union([
Type.String(),
Type.Null()
]),
deleted_at: Type.Optional(Type.Union([
DateTransform({ format: 'date-time' }),
Type.Null()
])),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: DateTransform({ format: 'date-time' }),
logoUrl: Type.Optional(Type.String({ format: 'uri' })),
logoFileId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
]))
})
}))
});

export type CreateAppParams = Static<typeof CreateAppParamsSchema>;
export type CreateAppBody = Static<typeof CreateAppBodySchema>;
export type CreateAppResponse = Static<typeof CreateAppResponseSchema>;

export const createApp = {
  method: 'POST',
  path: '/apps',
  config: {
  schema: {
  params: CreateAppParamsSchema,
  body: CreateAppBodySchema,
  response: {
  201: Type.Object({
data: Type.Optional(Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('apps')
]),
attributes: Type.Object({
name: Type.String(),
appUserId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
serviceAccountUuid: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
entityId: Type.Number(),
entityUuid: Type.String(),
entityName: Type.String(),
clientId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
clientSecret: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
signingSecret: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
url: Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
]),
installUrl: Type.Optional(Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
])),
ownerUserId: Type.Union([
Type.String(),
Type.Null()
]),
description: Type.Union([
Type.String(),
Type.Null()
]),
deleted_at: Type.Optional(Type.Union([
DateTransform({ format: 'date-time' }),
Type.Null()
])),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: DateTransform({ format: 'date-time' }),
logoUrl: Type.Optional(Type.String({ format: 'uri' })),
logoFileId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
]))
})
}))
}),
  400: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  401: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  403: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  406: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  409: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
})
) /* array of items */)
}),
  415: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  500: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
},
  headers: Type.Object({}),
},
}
} as const satisfies RouteConfig;

export const GetAppByIdParamsSchema = Type.Object({
id: Type.String()
});

export const GetAppByIdResponseSchema = Type.Object({
data: Type.Optional(Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('apps')
]),
attributes: Type.Object({
name: Type.String(),
appUserId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
serviceAccountUuid: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
entityId: Type.Number(),
entityUuid: Type.String(),
entityName: Type.String(),
clientId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
clientSecret: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
signingSecret: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
url: Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
]),
installUrl: Type.Optional(Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
])),
ownerUserId: Type.Union([
Type.String(),
Type.Null()
]),
description: Type.Union([
Type.String(),
Type.Null()
]),
deleted_at: Type.Optional(Type.Union([
DateTransform({ format: 'date-time' }),
Type.Null()
])),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: DateTransform({ format: 'date-time' }),
logoUrl: Type.Optional(Type.String({ format: 'uri' })),
logoFileId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
]))
})
}))
});

export type GetAppByIdParams = Static<typeof GetAppByIdParamsSchema>;
export type GetAppByIdResponse = Static<typeof GetAppByIdResponseSchema>;

export const getAppById = {
  method: 'GET',
  path: '/apps/:id',
  config: {
  schema: {
  params: GetAppByIdParamsSchema,
  response: {
  200: Type.Object({
data: Type.Optional(Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('apps')
]),
attributes: Type.Object({
name: Type.String(),
appUserId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
serviceAccountUuid: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
entityId: Type.Number(),
entityUuid: Type.String(),
entityName: Type.String(),
clientId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
clientSecret: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
signingSecret: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
url: Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
]),
installUrl: Type.Optional(Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
])),
ownerUserId: Type.Union([
Type.String(),
Type.Null()
]),
description: Type.Union([
Type.String(),
Type.Null()
]),
deleted_at: Type.Optional(Type.Union([
DateTransform({ format: 'date-time' }),
Type.Null()
])),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: DateTransform({ format: 'date-time' }),
logoUrl: Type.Optional(Type.String({ format: 'uri' })),
logoFileId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
]))
})
}))
}),
  400: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  401: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  403: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  404: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  406: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  500: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
},
  headers: Type.Object({}),
},
}
} as const satisfies RouteConfig;

export const UpdateAppParamsSchema = Type.Object({
id: Type.String()
});

export const UpdateAppBodySchema = Type.Object({
data: Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('apps')
]),
attributes: Type.Object({
name: Type.Optional(Type.String()),
url: Type.Optional(Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
])),
description: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
logoUrl: Type.Optional(Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
])),
logoFileId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
]))
})
})
});

export const UpdateAppResponseSchema = Type.Object({
data: Type.Optional(Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('apps')
]),
attributes: Type.Object({
name: Type.String(),
appUserId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
serviceAccountUuid: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
entityId: Type.Number(),
entityUuid: Type.String(),
entityName: Type.String(),
clientId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
clientSecret: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
signingSecret: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
url: Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
]),
installUrl: Type.Optional(Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
])),
ownerUserId: Type.Union([
Type.String(),
Type.Null()
]),
description: Type.Union([
Type.String(),
Type.Null()
]),
deleted_at: Type.Optional(Type.Union([
DateTransform({ format: 'date-time' }),
Type.Null()
])),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: DateTransform({ format: 'date-time' }),
logoUrl: Type.Optional(Type.String({ format: 'uri' })),
logoFileId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
]))
})
}))
});

export type UpdateAppParams = Static<typeof UpdateAppParamsSchema>;
export type UpdateAppBody = Static<typeof UpdateAppBodySchema>;
export type UpdateAppResponse = Static<typeof UpdateAppResponseSchema>;

export const updateApp = {
  method: 'PATCH',
  path: '/apps/:id',
  config: {
  schema: {
  params: UpdateAppParamsSchema,
  body: UpdateAppBodySchema,
  response: {
  200: Type.Object({
data: Type.Optional(Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('apps')
]),
attributes: Type.Object({
name: Type.String(),
appUserId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
serviceAccountUuid: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
entityId: Type.Number(),
entityUuid: Type.String(),
entityName: Type.String(),
clientId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
clientSecret: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
signingSecret: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
url: Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
]),
installUrl: Type.Optional(Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
])),
ownerUserId: Type.Union([
Type.String(),
Type.Null()
]),
description: Type.Union([
Type.String(),
Type.Null()
]),
deleted_at: Type.Optional(Type.Union([
DateTransform({ format: 'date-time' }),
Type.Null()
])),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: DateTransform({ format: 'date-time' }),
logoUrl: Type.Optional(Type.String({ format: 'uri' })),
logoFileId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
]))
})
}))
}),
  400: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  401: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  403: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  404: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  406: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  409: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
})
) /* array of items */)
}),
  415: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  500: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
},
  headers: Type.Object({}),
},
}
} as const satisfies RouteConfig;

export const DeleteAppParamsSchema = Type.Object({
id: Type.String()
});

export const DeleteAppResponseSchema = Type.Object({});

export type DeleteAppParams = Static<typeof DeleteAppParamsSchema>;
export type DeleteAppResponse = Static<typeof DeleteAppResponseSchema>;

export const deleteApp = {
  method: 'DELETE',
  path: '/apps/:id/delete',
  config: {
  schema: {
  params: DeleteAppParamsSchema,
  response: {
  204: Type.Object({}),
  400: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  401: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  403: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  404: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  406: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  500: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
},
  headers: Type.Object({}),
},
}
} as const satisfies RouteConfig;

export const GetAppPermissionsParamsSchema = Type.Object({
id: Type.String()
});

export const GetAppPermissionsResponseSchema = Type.Object({
data: Type.Optional(Type.Array(
Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('permission')
]),
attributes: Type.Object({
role: Type.String(),
displayName: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
description: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
suiteId: Type.String(),
suiteName: Type.Optional(Type.String())
})
})
) /* array of items */)
});

export type GetAppPermissionsParams = Static<typeof GetAppPermissionsParamsSchema>;
export type GetAppPermissionsResponse = Static<typeof GetAppPermissionsResponseSchema>;

export const getAppPermissions = {
  method: 'GET',
  path: '/apps/:id/permissions',
  config: {
  schema: {
  params: GetAppPermissionsParamsSchema,
  response: {
  200: Type.Object({
data: Type.Optional(Type.Array(
Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('permission')
]),
attributes: Type.Object({
role: Type.String(),
displayName: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
description: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
suiteId: Type.String(),
suiteName: Type.Optional(Type.String())
})
})
) /* array of items */)
}),
  400: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  401: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  403: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  404: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  406: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  500: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
},
  headers: Type.Object({}),
},
}
} as const satisfies RouteConfig;

export const SetAppPermissionsParamsSchema = Type.Object({
id: Type.String()
});

export const SetAppPermissionsBodySchema = Type.Object({
data: Type.Object({
type: Type.Union([
Type.Literal('permissions')
]),
attributes: Type.Object({
permissionIds: Type.Optional(Type.String()),
entityUuid: Type.String()
})
})
});

export const SetAppPermissionsResponseSchema = Type.Object({
data: Type.Optional(Type.Array(
Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('permission')
]),
attributes: Type.Object({
role: Type.String(),
displayName: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
description: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
suiteId: Type.String(),
suiteName: Type.Optional(Type.String())
})
})
) /* array of items */)
});

export type SetAppPermissionsParams = Static<typeof SetAppPermissionsParamsSchema>;
export type SetAppPermissionsBody = Static<typeof SetAppPermissionsBodySchema>;
export type SetAppPermissionsResponse = Static<typeof SetAppPermissionsResponseSchema>;

export const setAppPermissions = {
  method: 'PUT',
  path: '/apps/:id/permissions',
  config: {
  schema: {
  params: SetAppPermissionsParamsSchema,
  body: SetAppPermissionsBodySchema,
  response: {
  200: Type.Object({
data: Type.Optional(Type.Array(
Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('permission')
]),
attributes: Type.Object({
role: Type.String(),
displayName: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
description: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
suiteId: Type.String(),
suiteName: Type.Optional(Type.String())
})
})
) /* array of items */)
}),
  400: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  401: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  403: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  404: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  500: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
},
  headers: Type.Object({}),
},
}
} as const satisfies RouteConfig;

export const RemoveAppPermissionsParamsSchema = Type.Object({
id: Type.String()
});

export const RemoveAppPermissionsBodySchema = Type.Object({
data: Type.Object({
type: Type.Union([
Type.Literal('permissions')
]),
attributes: Type.Object({
entityUuid: Type.String()
})
})
});

export const RemoveAppPermissionsResponseSchema = Type.Object({});

export type RemoveAppPermissionsParams = Static<typeof RemoveAppPermissionsParamsSchema>;
export type RemoveAppPermissionsBody = Static<typeof RemoveAppPermissionsBodySchema>;
export type RemoveAppPermissionsResponse = Static<typeof RemoveAppPermissionsResponseSchema>;

export const removeAppPermissions = {
  method: 'DELETE',
  path: '/apps/:id/permissions',
  config: {
  schema: {
  params: RemoveAppPermissionsParamsSchema,
  body: RemoveAppPermissionsBodySchema,
  response: {
  204: Type.Object({}),
  400: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  401: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  403: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  404: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  500: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
},
  headers: Type.Object({}),
},
}
} as const satisfies RouteConfig;

export const UploadAppLogoParamsSchema = Type.Object({
id: Type.String()
});

export const UploadAppLogoResponseSchema = Type.Object({
data: Type.Optional(Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('apps')
]),
attributes: Type.Object({
name: Type.String(),
appUserId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
serviceAccountUuid: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
entityId: Type.Number(),
entityUuid: Type.String(),
entityName: Type.String(),
clientId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
clientSecret: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
signingSecret: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
url: Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
]),
installUrl: Type.Optional(Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
])),
ownerUserId: Type.Union([
Type.String(),
Type.Null()
]),
description: Type.Union([
Type.String(),
Type.Null()
]),
deleted_at: Type.Optional(Type.Union([
DateTransform({ format: 'date-time' }),
Type.Null()
])),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: DateTransform({ format: 'date-time' }),
logoUrl: Type.Optional(Type.String({ format: 'uri' })),
logoFileId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
]))
})
}))
});

export type UploadAppLogoParams = Static<typeof UploadAppLogoParamsSchema>;
export type UploadAppLogoResponse = Static<typeof UploadAppLogoResponseSchema>;

export const uploadAppLogo = {
  method: 'POST',
  path: '/apps/:id/logo',
  config: {
  schema: {
  params: UploadAppLogoParamsSchema,
  response: {
  201: Type.Object({
data: Type.Optional(Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('apps')
]),
attributes: Type.Object({
name: Type.String(),
appUserId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
serviceAccountUuid: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
entityId: Type.Number(),
entityUuid: Type.String(),
entityName: Type.String(),
clientId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
clientSecret: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
signingSecret: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
url: Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
]),
installUrl: Type.Optional(Type.Union([
Type.String({ format: 'uri' }),
Type.Null()
])),
ownerUserId: Type.Union([
Type.String(),
Type.Null()
]),
description: Type.Union([
Type.String(),
Type.Null()
]),
deleted_at: Type.Optional(Type.Union([
DateTransform({ format: 'date-time' }),
Type.Null()
])),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: DateTransform({ format: 'date-time' }),
logoUrl: Type.Optional(Type.String({ format: 'uri' })),
logoFileId: Type.Optional(Type.Union([
Type.String(),
Type.Null()
]))
})
}))
}),
  400: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  401: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  403: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  404: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  500: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
},
  headers: Type.Object({}),
},
}
} as const satisfies RouteConfig;

export const DeleteAppLogoParamsSchema = Type.Object({
id: Type.String()
});

export const DeleteAppLogoResponseSchema = Type.Object({});

export type DeleteAppLogoParams = Static<typeof DeleteAppLogoParamsSchema>;
export type DeleteAppLogoResponse = Static<typeof DeleteAppLogoResponseSchema>;

export const deleteAppLogo = {
  method: 'DELETE',
  path: '/apps/:id/logo',
  config: {
  schema: {
  params: DeleteAppLogoParamsSchema,
  response: {
  204: Type.Object({}),
  400: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  401: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  403: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  404: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  500: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
},
  headers: Type.Object({}),
},
}
} as const satisfies RouteConfig;

export const GetApiKeySummariesParamsSchema = Type.Object({});

export const GetApiKeySummariesQuerySchema = Type.Object({
'filter[appID]': Type.Optional(Type.String())
});

export const GetApiKeySummariesResponseSchema = Type.Object({
data: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
type: Type.Optional(Type.Union([
Type.Literal('apiKeySummary')
])),
attributes: Type.Optional(Type.Object({
keySuffix: Type.String(),
name: Type.String(),
description: Type.Union([
Type.String(),
Type.Null()
]),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: Type.Optional(DateTransform({ format: 'date-time' })),
appID: Type.String(),
accessToken: Type.Optional(Type.String())
}))
})
) /* array of items */)
});

export type GetApiKeySummariesParams = Static<typeof GetApiKeySummariesParamsSchema>;
export type GetApiKeySummariesQuery = Static<typeof GetApiKeySummariesQuerySchema>;
export type GetApiKeySummariesResponse = Static<typeof GetApiKeySummariesResponseSchema>;

export const getApiKeySummaries = {
  method: 'GET',
  path: '/api-key-summaries',
  config: {
  schema: {
  params: GetApiKeySummariesParamsSchema,
  querystring: GetApiKeySummariesQuerySchema,
  response: {
  200: Type.Object({
data: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
type: Type.Optional(Type.Union([
Type.Literal('apiKeySummary')
])),
attributes: Type.Optional(Type.Object({
keySuffix: Type.String(),
name: Type.String(),
description: Type.Union([
Type.String(),
Type.Null()
]),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: Type.Optional(DateTransform({ format: 'date-time' })),
appID: Type.String(),
accessToken: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  400: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  401: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  403: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  406: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  500: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
},
  headers: Type.Object({}),
},
}
} as const satisfies RouteConfig;

export const CreateApiKeySummaryParamsSchema = Type.Object({});

export const CreateApiKeySummaryBodySchema = Type.Object({
data: Type.Object({
type: Type.Union([
Type.Literal('apiKeySummary')
]),
attributes: Type.Object({
name: Type.String(),
appID: Type.String(),
serviceAccountUuid: Type.String(),
entityId: Type.Number()
})
})
});

export const CreateApiKeySummaryResponseSchema = Type.Object({
data: Type.Object({
id: Type.Optional(Type.String()),
type: Type.Optional(Type.Union([
Type.Literal('apiKeySummary')
])),
attributes: Type.Optional(Type.Object({
keySuffix: Type.String(),
name: Type.String(),
description: Type.Union([
Type.String(),
Type.Null()
]),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: Type.Optional(DateTransform({ format: 'date-time' })),
appID: Type.String(),
accessToken: Type.Optional(Type.String())
}))
})
});

export type CreateApiKeySummaryParams = Static<typeof CreateApiKeySummaryParamsSchema>;
export type CreateApiKeySummaryBody = Static<typeof CreateApiKeySummaryBodySchema>;
export type CreateApiKeySummaryResponse = Static<typeof CreateApiKeySummaryResponseSchema>;

export const createApiKeySummary = {
  method: 'POST',
  path: '/api-key-summaries',
  config: {
  schema: {
  params: CreateApiKeySummaryParamsSchema,
  body: CreateApiKeySummaryBodySchema,
  response: {
  201: Type.Object({
data: Type.Object({
id: Type.Optional(Type.String()),
type: Type.Optional(Type.Union([
Type.Literal('apiKeySummary')
])),
attributes: Type.Optional(Type.Object({
keySuffix: Type.String(),
name: Type.String(),
description: Type.Union([
Type.String(),
Type.Null()
]),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: Type.Optional(DateTransform({ format: 'date-time' })),
appID: Type.String(),
accessToken: Type.Optional(Type.String())
}))
})
}),
  400: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  401: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  403: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  409: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
})
) /* array of items */)
}),
  415: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  500: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
},
  headers: Type.Object({}),
},
}
} as const satisfies RouteConfig;

export const GetApiKeySummaryByIdParamsSchema = Type.Object({
id: Type.String()
});

export const GetApiKeySummaryByIdResponseSchema = Type.Object({
data: Type.Object({
id: Type.Optional(Type.String()),
type: Type.Optional(Type.Union([
Type.Literal('apiKeySummary')
])),
attributes: Type.Optional(Type.Object({
keySuffix: Type.String(),
name: Type.String(),
description: Type.Union([
Type.String(),
Type.Null()
]),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: Type.Optional(DateTransform({ format: 'date-time' })),
appID: Type.String(),
accessToken: Type.Optional(Type.String())
}))
})
});

export type GetApiKeySummaryByIdParams = Static<typeof GetApiKeySummaryByIdParamsSchema>;
export type GetApiKeySummaryByIdResponse = Static<typeof GetApiKeySummaryByIdResponseSchema>;

export const getApiKeySummaryById = {
  method: 'GET',
  path: '/api-key-summaries/:id',
  config: {
  schema: {
  params: GetApiKeySummaryByIdParamsSchema,
  response: {
  200: Type.Object({
data: Type.Object({
id: Type.Optional(Type.String()),
type: Type.Optional(Type.Union([
Type.Literal('apiKeySummary')
])),
attributes: Type.Optional(Type.Object({
keySuffix: Type.String(),
name: Type.String(),
description: Type.Union([
Type.String(),
Type.Null()
]),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: Type.Optional(DateTransform({ format: 'date-time' })),
appID: Type.String(),
accessToken: Type.Optional(Type.String())
}))
})
}),
  400: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  401: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  403: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  404: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  406: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  500: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
},
  headers: Type.Object({}),
},
}
} as const satisfies RouteConfig;

export const UpdateApiKeySummaryParamsSchema = Type.Object({
id: Type.String()
});

export const UpdateApiKeySummaryBodySchema = Type.Object({
data: Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('apiKeySummary')
]),
attributes: Type.Object({
name: Type.Optional(Type.String()),
description: Type.Optional(Type.String())
})
})
});

export const UpdateApiKeySummaryResponseSchema = Type.Object({
data: Type.Object({
id: Type.Optional(Type.String()),
type: Type.Optional(Type.Union([
Type.Literal('apiKeySummary')
])),
attributes: Type.Optional(Type.Object({
keySuffix: Type.String(),
name: Type.String(),
description: Type.Union([
Type.String(),
Type.Null()
]),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: Type.Optional(DateTransform({ format: 'date-time' })),
appID: Type.String(),
accessToken: Type.Optional(Type.String())
}))
})
});

export type UpdateApiKeySummaryParams = Static<typeof UpdateApiKeySummaryParamsSchema>;
export type UpdateApiKeySummaryBody = Static<typeof UpdateApiKeySummaryBodySchema>;
export type UpdateApiKeySummaryResponse = Static<typeof UpdateApiKeySummaryResponseSchema>;

export const updateApiKeySummary = {
  method: 'PATCH',
  path: '/api-key-summaries/:id',
  config: {
  schema: {
  params: UpdateApiKeySummaryParamsSchema,
  body: UpdateApiKeySummaryBodySchema,
  response: {
  200: Type.Object({
data: Type.Object({
id: Type.Optional(Type.String()),
type: Type.Optional(Type.Union([
Type.Literal('apiKeySummary')
])),
attributes: Type.Optional(Type.Object({
keySuffix: Type.String(),
name: Type.String(),
description: Type.Union([
Type.String(),
Type.Null()
]),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: Type.Optional(DateTransform({ format: 'date-time' })),
appID: Type.String(),
accessToken: Type.Optional(Type.String())
}))
})
}),
  400: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  401: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  403: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  404: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  406: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  409: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
})
) /* array of items */)
}),
  415: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  500: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
},
  headers: Type.Object({}),
},
}
} as const satisfies RouteConfig;

export const DeleteApiKeySummaryParamsSchema = Type.Object({
id: Type.String()
});

export const DeleteApiKeySummaryResponseSchema = Type.Object({});

export type DeleteApiKeySummaryParams = Static<typeof DeleteApiKeySummaryParamsSchema>;
export type DeleteApiKeySummaryResponse = Static<typeof DeleteApiKeySummaryResponseSchema>;

export const deleteApiKeySummary = {
  method: 'DELETE',
  path: '/api-key-summaries/:id',
  config: {
  schema: {
  params: DeleteApiKeySummaryParamsSchema,
  response: {
  204: Type.Object({}),
  400: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  401: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  403: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  404: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  500: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
},
  headers: Type.Object({}),
},
}
} as const satisfies RouteConfig;

export const GetEntitiesParamsSchema = Type.Object({});

export const GetEntitiesQuerySchema = Type.Object({
'filter[claim]': Type.Optional(Type.String())
});

export const GetEntitiesResponseSchema = Type.Object({
data: Type.Array(
Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('entity')
]),
attributes: Type.Object({
name: Type.String(),
displayName: Type.String(),
entityType: Type.String(),
entityId: Type.Number(),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: DateTransform({ format: 'date-time' })
})
})
) /* array of items */,
meta: Type.Object({
hasWildcard: Type.Optional(Type.Boolean())
})
});

export type GetEntitiesParams = Static<typeof GetEntitiesParamsSchema>;
export type GetEntitiesQuery = Static<typeof GetEntitiesQuerySchema>;
export type GetEntitiesResponse = Static<typeof GetEntitiesResponseSchema>;

export const getEntities = {
  method: 'GET',
  path: '/entities',
  config: {
  schema: {
  params: GetEntitiesParamsSchema,
  querystring: GetEntitiesQuerySchema,
  response: {
  200: Type.Object({
data: Type.Array(
Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('entity')
]),
attributes: Type.Object({
name: Type.String(),
displayName: Type.String(),
entityType: Type.String(),
entityId: Type.Number(),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: DateTransform({ format: 'date-time' })
})
})
) /* array of items */,
meta: Type.Object({
hasWildcard: Type.Optional(Type.Boolean())
})
}),
  400: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  401: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  403: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  406: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  500: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
},
  headers: Type.Object({}),
},
}
} as const satisfies RouteConfig;

export const GetEntityByIdParamsSchema = Type.Object({
id: Type.String()
});

export const GetEntityByIdResponseSchema = Type.Object({
data: Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('entity')
]),
attributes: Type.Object({
name: Type.String(),
displayName: Type.String(),
entityType: Type.String(),
entityId: Type.Number(),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: DateTransform({ format: 'date-time' })
})
})
});

export type GetEntityByIdParams = Static<typeof GetEntityByIdParamsSchema>;
export type GetEntityByIdResponse = Static<typeof GetEntityByIdResponseSchema>;

export const getEntityById = {
  method: 'GET',
  path: '/entities/:id',
  config: {
  schema: {
  params: GetEntityByIdParamsSchema,
  response: {
  200: Type.Object({
data: Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('entity')
]),
attributes: Type.Object({
name: Type.String(),
displayName: Type.String(),
entityType: Type.String(),
entityId: Type.Number(),
createdAt: DateTransform({ format: 'date-time' }),
updatedAt: DateTransform({ format: 'date-time' })
})
})
}),
  400: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  401: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  403: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  404: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  406: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  500: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
},
  headers: Type.Object({}),
},
}
} as const satisfies RouteConfig;

export const GetPermissionsParamsSchema = Type.Object({});

export const GetPermissionsQuerySchema = Type.Object({
'filter[suiteId]': Type.Optional(Type.String()),
'filter[suiteInternalName]': Type.Optional(Type.String())
});

export const GetPermissionsResponseSchema = Type.Object({
data: Type.Optional(Type.Array(
Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('permission')
]),
attributes: Type.Object({
role: Type.String(),
displayName: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
description: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
suiteId: Type.String(),
suiteName: Type.Optional(Type.String())
})
})
) /* array of items */)
});

export type GetPermissionsParams = Static<typeof GetPermissionsParamsSchema>;
export type GetPermissionsQuery = Static<typeof GetPermissionsQuerySchema>;
export type GetPermissionsResponse = Static<typeof GetPermissionsResponseSchema>;

export const getPermissions = {
  method: 'GET',
  path: '/permissions',
  config: {
  schema: {
  params: GetPermissionsParamsSchema,
  querystring: GetPermissionsQuerySchema,
  response: {
  200: Type.Object({
data: Type.Optional(Type.Array(
Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('permission')
]),
attributes: Type.Object({
role: Type.String(),
displayName: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
description: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
suiteId: Type.String(),
suiteName: Type.Optional(Type.String())
})
})
) /* array of items */)
}),
  400: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  401: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  403: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  406: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  500: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
},
  headers: Type.Object({}),
},
}
} as const satisfies RouteConfig;

export const GetPermissionByIdParamsSchema = Type.Object({
id: Type.String()
});

export const GetPermissionByIdResponseSchema = Type.Object({
data: Type.Optional(Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('permission')
]),
attributes: Type.Object({
role: Type.String(),
displayName: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
description: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
suiteId: Type.String(),
suiteName: Type.Optional(Type.String())
})
}))
});

export type GetPermissionByIdParams = Static<typeof GetPermissionByIdParamsSchema>;
export type GetPermissionByIdResponse = Static<typeof GetPermissionByIdResponseSchema>;

export const getPermissionById = {
  method: 'GET',
  path: '/permissions/:id',
  config: {
  schema: {
  params: GetPermissionByIdParamsSchema,
  response: {
  200: Type.Object({
data: Type.Optional(Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('permission')
]),
attributes: Type.Object({
role: Type.String(),
displayName: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
description: Type.Optional(Type.Union([
Type.String(),
Type.Null()
])),
suiteId: Type.String(),
suiteName: Type.Optional(Type.String())
})
}))
}),
  400: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  401: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  403: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  404: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  500: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
},
  headers: Type.Object({}),
},
}
} as const satisfies RouteConfig;

export const GetSuitesParamsSchema = Type.Object({});

export const GetSuitesResponseSchema = Type.Object({
data: Type.Optional(Type.Array(
Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('suite')
]),
attributes: Type.Object({
internalName: Type.String(),
displayName: Type.String()
})
})
) /* array of items */)
});

export type GetSuitesParams = Static<typeof GetSuitesParamsSchema>;
export type GetSuitesResponse = Static<typeof GetSuitesResponseSchema>;

export const getSuites = {
  method: 'GET',
  path: '/suites',
  config: {
  schema: {
  params: GetSuitesParamsSchema,
  response: {
  200: Type.Object({
data: Type.Optional(Type.Array(
Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('suite')
]),
attributes: Type.Object({
internalName: Type.String(),
displayName: Type.String()
})
})
) /* array of items */)
}),
  400: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  401: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  403: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  500: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
},
  headers: Type.Object({}),
},
}
} as const satisfies RouteConfig;

export const GetSuiteByIdParamsSchema = Type.Object({
id: Type.String()
});

export const GetSuiteByIdResponseSchema = Type.Object({
data: Type.Optional(Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('suite')
]),
attributes: Type.Object({
internalName: Type.String(),
displayName: Type.String()
})
}))
});

export type GetSuiteByIdParams = Static<typeof GetSuiteByIdParamsSchema>;
export type GetSuiteByIdResponse = Static<typeof GetSuiteByIdResponseSchema>;

export const getSuiteById = {
  method: 'GET',
  path: '/suites/:id',
  config: {
  schema: {
  params: GetSuiteByIdParamsSchema,
  response: {
  200: Type.Object({
data: Type.Optional(Type.Object({
id: Type.String(),
type: Type.Union([
Type.Literal('suite')
]),
attributes: Type.Object({
internalName: Type.String(),
displayName: Type.String()
})
}))
}),
  400: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  401: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  403: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  404: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
  500: Type.Object({
errors: Type.Optional(Type.Array(
Type.Object({
id: Type.Optional(Type.String()),
code: Type.Optional(Type.String()),
status: Type.Optional(Type.String()),
title: Type.Optional(Type.String()),
detail: Type.Optional(Type.String()),
source: Type.Optional(Type.Object({
pointer: Type.Optional(Type.String()),
parameter: Type.Optional(Type.String()),
header: Type.Optional(Type.String())
}))
})
) /* array of items */)
}),
},
  headers: Type.Object({}),
},
}
} as const satisfies RouteConfig;

